import { LoadingMessage } from '@communities/community/components';
import { useCommunityDetail } from '@communities/community/hooks';
import { HeroBackground } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Container, Section, Heading, Description } from '@soteria/design-system-ve';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import * as styles from './ClassificationTestingPage.css';
import { ClassificationContentInputSection } from './sections/ClassificationContentInputSection';
import { ClassificationResultsSection } from './sections/ClassificationResultsSection';

/**
 * ClassificationTestingPage component
 *
 * Main page for testing content classification models.
 */
function ClassificationTestingPageComponent() {
  const location = useLocation();

  // Extract prompt version from URL query parameters
  const promptVersion = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    return searchParams.get('promptVersion') || undefined;
  }, [location.search]);

  // Get community data directly from the community ID in the URL
  const { community, loading } = useCommunityDetail();

  if (loading) {
    return <LoadingMessage />;
  }

  // Use the community name from the hook
  const communityName = community ? community.name : 'Unknown Community';

  return (
    <>
      <HeroBackground showGradient imageUrl={community?.superHeroArtUrl} />
      <Container className={styles.withHeroBackground} padding="xxlarge" size="xxl">
        <Section spacing="medium">
          <div>
            <Heading className={styles.titleWithHero} level="h1">
              Classification Testing
            </Heading>
            <Description className={styles.communityInfoWithHero}>Test content classification for {communityName}</Description>
          </div>
        </Section>

        <div className={styles.mainContentLayout}>
          <div className={styles.contentInputSection}>
            <ClassificationContentInputSection communityId={community?.id} promptVersion={promptVersion} />
          </div>
          <div className={styles.classificationResultsSection}>
            <ClassificationResultsSection communityId={community?.id} communityName={communityName} />
          </div>
        </div>
      </Container>
    </>
  );
}

export const ClassificationTestingPage = withAppInsightsTracking(ClassificationTestingPageComponent, 'ClassificationTestingPage');
